import { fetchPage, postPage, deletePage } from "@/services/config";
import type {
  ContractLineResData,
  ContractResData,
  CustomerLoginResData,
  CustomerResData,
  InvoiceLineResData,
  InvoiceResData,
  ProductResData,
  RawContractData,
  RawContractLineCreateData,
  RawCustomerData,
  RawInvoiceCreateData,
  RawInvoiceData,
  RawInvoiceLineCreateData,
  RawLoginUser,
  RawPortalSettings,
  RawProductData
} from "@/types";

const serviceBase = "psa";


// Portal settings
export function getPortalSettings(tenantId: number): Promise<RawPortalSettings> {
  return fetchPage<RawPortalSettings>(serviceBase, `/${tenantId}/config/portal`);
}

export function editPortalSettings(
  tenantId: number,
  payload: Record<string, unknown>
): Promise<RawPortalSettings> {
  return postPage<RawPortalSettings>(serviceBase, `/${tenantId}/config/portal`, payload);
}

export function verifyPortalDomain(tenantId: number, domainName: string): Promise<boolean> {
  return fetchPage<boolean>(serviceBase, `/${tenantId}/config/portal/verify-domain`, {
    domain_name: domainName
  });
}


// Customers
export function getAllCustomers(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<CustomerResData> {
  return fetchPage<CustomerResData>(serviceBase, `/${tenantId}/customer`, params);
}

export function getCustomer(tenantId: number, customerId: string): Promise<unknown> {
  return fetchPage(serviceBase, `/${tenantId}/customer/${customerId}`);
}

export function addCustomer(tenantId: number, payload: RawCustomerData): Promise<RawCustomerData> {
  return postPage<RawCustomerData>(serviceBase, `/${tenantId}/customer`, payload);
}

export function editCustomer(
  tenantId: number, customerId: string, payload: RawCustomerData
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}`, payload, {}, "PATCH");
}

export function deleteCustomer(tenantId: number, customerId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/customer/${customerId}`);
}


// Login users
export function getAllLoginUsers(
  tenantId: number,
  customerId: string,
  params: Record<string, unknown> = {}
): Promise<CustomerLoginResData> {
  return fetchPage<CustomerLoginResData>(
    serviceBase,
    `/${tenantId}/customer/${customerId}/login`,
    params
  );
}

export function addLoginUser(
  tenantId: number,
  customerId: string,
  payload: RawLoginUser
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}/login`, payload);
}

export function editLoginUser(
  tenantId: number,
  customerId: string,
  loginId: string, // Currently using email as the login id
  payload: RawLoginUser
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/customer/${customerId}/login/${loginId}`, payload, {}, "PATCH");
}

export function deleteLoginUser(
  tenantId: number,
  customerId: string,
  loginId: string
): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/customer/${customerId}/login/${loginId}`);
}


//  Invoices
export function getAllInvoices(
  tenantId: number, params: Record<string, unknown> = {}
): Promise<InvoiceResData> {
  return fetchPage<InvoiceResData>(serviceBase, `/${tenantId}/invoice`, params);
}

export function getInvoice(tenantId: number, invoiceId: string): Promise<RawInvoiceData> {
  return fetchPage<RawInvoiceData>(serviceBase, `/${tenantId}/invoice/${invoiceId}`);
}

export function addInvoice(
  tenantId: number, payload: RawInvoiceCreateData
): Promise<RawInvoiceData> {
  return postPage<RawInvoiceData>(serviceBase, `/${tenantId}/invoice`, payload);
}

export function editInvoice(
  tenantId: number, invoiceId: string, payload: Partial<RawInvoiceCreateData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/invoice/${invoiceId}`, payload, {}, "PATCH");
}

export function deleteInvoice(tenantId: number, invoiceId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/invoice/${invoiceId}`);
}

export function getInvoiceLines(
  tenantId: number,
  invoiceId: string,
  params: Record<string, unknown> = {}
): Promise<InvoiceLineResData> {
  return fetchPage<InvoiceLineResData>(serviceBase, `/${tenantId}/invoice/${invoiceId}/line`, params);
}

export function addInvoiceLine(
  tenantId: number, invoiceId: string, payload: RawInvoiceLineCreateData
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/invoice/${invoiceId}/line`, payload);
}

export function editInvoiceLine(
  tenantId: number, invoiceId: string, lineNum: number, payload: Partial<RawInvoiceLineCreateData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/invoice/${invoiceId}/line/${lineNum}`, payload, {}, "PATCH");
}

export function deleteInvoiceLine(
  tenantId: number, invoiceId: string, lineNum: number
): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/invoice/${invoiceId}/line/${lineNum}`);
}


// Contracts
export function getAllContracts(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<ContractResData> {
  return fetchPage<ContractResData>(serviceBase, `/${tenantId}/contract`, params);
}

export function getContract(tenantId: number, contractId: string): Promise<RawContractData> {
  return fetchPage<RawContractData>(serviceBase, `/${tenantId}/contract/${contractId}`);
}

export function addContract(tenantId: number, payload: RawContractData): Promise<RawContractData> {
  return postPage<RawContractData>(serviceBase, `/${tenantId}/contract`, payload);
}

export function editContract(
  tenantId: number, contractId: string, payload: Partial<RawContractData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract/${contractId}`, payload, {}, "PATCH");
}

export function deleteContract(tenantId: number, contractId: string): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/contract/${contractId}`);
}

export function cancelContract(tenantId: number, contractId: string): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract/${contractId}/cancel`);
}

export function getContractLines(
  tenantId: number,
  contractId: string,
  params: Record<string, unknown> = {}
): Promise<ContractLineResData> {
  return fetchPage<ContractLineResData>(serviceBase, `/${tenantId}/contract/${contractId}/line`, params);
}

export function addContractLine(
  tenantId: number, contractId: string, payload: RawContractLineCreateData
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract/${contractId}/line`, payload);
}

export function editContractLine(
  tenantId: number, contractId: string, lineNum: number, payload: Partial<RawContractLineCreateData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/contract/${contractId}/line/${lineNum}`, payload, {}, "PATCH");
}

export function deleteContractLine(
  tenantId: number, contractId: string, lineNum: number
): Promise<unknown> {
  return deletePage(serviceBase, `/${tenantId}/contract/${contractId}/line/${lineNum}`);
}


// Products
export function getAllProducts(
  tenantId: number,
  params: Record<string, unknown> = {}
): Promise<ProductResData> {
  return fetchPage<ProductResData>(serviceBase, `/${tenantId}/product`, params);
}

export function getProduct(tenantId: number, productId: string): Promise<RawProductData> {
  return fetchPage<RawProductData>(serviceBase, `/${tenantId}/product/${productId}`);
}

export function addProduct(tenantId: number, payload: RawProductData): Promise<RawProductData> {
  return postPage<RawProductData>(serviceBase, `/${tenantId}/product`, payload);
}

export function editProduct(
  tenantId: number, productId: string, payload: Partial<RawProductData>
): Promise<unknown> {
  return postPage(serviceBase, `/${tenantId}/product/${productId}`, payload, {}, "PATCH");
}

export function archiveProduct(tenantId: number, productId: string): Promise<RawProductData> {
  return postPage<RawProductData>(serviceBase, `/${tenantId}/product/${productId}/archive`);
}
